import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import { Investment } from '../../types/Investment';
import { StandardButton } from '../ui';

interface InvestmentGridCardProps {
  investment: Investment;
  isSelected: boolean;
  onEdit: (investment: Investment) => void;
  onDelete: (id: string) => void;
  onToggle?: (id: string, enabled: boolean) => void;
  onToggleSelection: (id: string) => void;
  useScenarioInvestments: boolean;
  formatCost: (cost: number) => string;
}

export const InvestmentGridCard: React.FC<InvestmentGridCardProps> = React.memo(({
  investment,
  isSelected,
  onEdit,
  onDelete,
  onToggle,
  onToggleSelection,
  useScenarioInvestments,
  formatCost
}) => {
  const { isDark } = useTheme();

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className={`rounded-lg shadow-sm border hover:shadow-md transition-shadow ${
        isDark 
          ? 'bg-white/[0.03] border-white/10 hover:bg-white/[0.05]' 
          : 'bg-white border-gray-200'
      }`}
    >
      <div className="p-4 flex flex-col h-full">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{investment.icon}</span>
          </div>
          <div className="flex gap-1">
            <StandardButton
              type="button"
              onClick={() => onEdit(investment)}
              variant="ghost"
              size="small"
              className="!p-1.5"
              title="Edit investment"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
              </svg>
            </StandardButton>
            <StandardButton
              type="button"
              onClick={() => onDelete(investment.id)}
              variant="ghost"
              size="small"
              className="!p-1.5 !text-red-400 hover:!text-red-300"
              title="Delete investment"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
              </svg>
            </StandardButton>
          </div>
        </div>

        {/* Content - Grows to fill space */}
        <div className="flex-1 space-y-2">
          <h3 className={`font-semibold ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            {investment.name}
          </h3>
          <p className={`text-sm ${
            isDark ? 'text-slate-300' : 'text-gray-600'
          }`}>
            {investment.description}
          </p>
          
          {/* Cost */}
          <div className={`text-lg font-bold mt-3 ${
            isDark ? 'text-green-400' : 'text-green-600'
          }`}>
            {formatCost(investment.cost)}
          </div>
        </div>

        {/* Bottom Section - Fixed at bottom */}
        <div className={`flex items-center justify-between mt-4 pt-4 border-t ${
          isDark ? 'border-white/10' : 'border-gray-200'
        }`}>
          {/* Checkbox - Bottom Left */}
          {useScenarioInvestments && onToggle && (
            <input
              type="checkbox"
              checked={investment.isActive}
              onChange={(e) => onToggle(investment.id, e.target.checked)}
              className={`rounded focus:ring-2 ${
                isDark 
                  ? 'border-white/20 text-professional-blue-400 focus:ring-professional-blue-400' 
                  : 'border-gray-300 text-blue-600 focus:ring-blue-500'
              }`}
              style={{ transform: 'scale(0.5)', transformOrigin: 'center' }}
            />
          )}
          {!useScenarioInvestments && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleSelection(investment.id)}
              className={`rounded focus:ring-2 ${
                isDark 
                  ? 'border-white/20 text-professional-blue-400 focus:ring-professional-blue-400' 
                  : 'border-gray-300 text-blue-600 focus:ring-blue-500'
              }`}
              style={{ transform: 'scale(0.5)', transformOrigin: 'center' }}
            />
          )}
          
          {/* Active Status - Bottom Right */}
          <span className={`text-xs font-medium ${
            investment.isActive 
              ? isDark ? 'text-green-400' : 'text-green-600' 
              : isDark ? 'text-red-400' : 'text-red-600'
          }`}>
            {investment.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>
    </motion.div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for better performance
  return (
    prevProps.investment.id === nextProps.investment.id &&
    prevProps.investment.name === nextProps.investment.name &&
    prevProps.investment.description === nextProps.investment.description &&
    prevProps.investment.icon === nextProps.investment.icon &&
    prevProps.investment.cost === nextProps.investment.cost &&
    prevProps.investment.isActive === nextProps.investment.isActive &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.useScenarioInvestments === nextProps.useScenarioInvestments &&
    prevProps.onEdit === nextProps.onEdit &&
    prevProps.onDelete === nextProps.onDelete &&
    prevProps.onToggle === nextProps.onToggle &&
    prevProps.onToggleSelection === nextProps.onToggleSelection
  );
});

InvestmentGridCard.displayName = 'InvestmentGridCard';